/**
 * What an inventory lot label encodes, and how to read one back.
 *
 * SPLIT OUT FOR THE SAME REASON `equipmentScanCode.ts` WAS: the mobile surface
 * has to resolve a scanned lot label, and the encoder it used to live beside
 * (`inventoryLabels.ts`) reaches `labelLayout` → `labelSymbol` → `bwip-js`.
 * That is a symbol GENERATOR, useless to a surface that only reads codes, and
 * pulling it into the `/m` entry would spend a large slice of the 250 KB budget
 * on a dependency the phone never calls. This module imports nothing.
 *
 * `expectedScanCodes` (pickList.ts) and `inventoryLabels.ts` both go through it,
 * so there is still exactly one definition: the value the printer writes and the
 * value the scanner checks can never drift apart. `cgt-backend`'s
 * `src/inventory/materials/lot-scan-code.ts` is its mirror on the side that
 * ENFORCES the format — change one, change both.
 *
 * TWO PARTS, NOT ONE. A lot number is only unique within its material, so the
 * material code has to ride along or the code names no single row.
 *
 * THE `LOT-` AFTER THE PIPE IS THE FORMAT'S OWN MARKER, not part of the lot
 * number. Server-minted numbers start with `LOT-` themselves, so a real label
 * reads `RPMI-1640|LOT-LOT-26-0001-1`. That looks like a stutter and is not one:
 * stripping only one `LOT-` is what makes the value round-trip back to the lot
 * number the backend stores.
 */

/** Separates the material code from the lot part. */
const SEPARATOR = '|'

/** Prefix on the lot half, mirroring the `LOC-`/`EQP-` convention. */
const LOT_PREFIX = 'LOT-'

export interface LotScanParts {
  materialCode: string
  lotNumber: string
}

export function lotScanCode(parts: {
  materialCode: string | null | undefined
  lotNumber: string | null | undefined
}): string | null {
  const materialCode = parts.materialCode?.trim()
  const lotNumber = parts.lotNumber?.trim()
  if (!materialCode || !lotNumber) return null
  return `${materialCode}${SEPARATOR}${LOT_PREFIX}${lotNumber}`
}

/**
 * Whether a scanned string is a lot label, and which lot it names.
 *
 * Returns null for anything that is not a lot code — an equipment or location
 * scan reaching a lot lookup is a mis-scan and must not resolve.
 *
 * CASE IS PRESERVED IN BOTH PARTS, deliberately. The marker test is
 * case-insensitive because a scanner or a typing operator may shift it, but both
 * keys are compared against case-sensitive columns server-side, so upper-casing
 * either hands back a key matching no row. Never feed this the output of
 * `normalizeScanValue`.
 */
export function lotFromScan(scanned: string | null | undefined): LotScanParts | null {
  const value = scanned?.trim()
  if (!value) return null

  const pipe = value.indexOf(SEPARATOR)
  if (pipe < 0) return null

  // THE TRIMS ON BOTH HALVES ARE INTENTIONAL, and are not carried over from the
  // inlined encoder this module replaced. A wedge scanner can pad either side of
  // the separator, and neither a material code nor a lot number ever legitimately
  // begins or ends in whitespace — so trimming can only ever turn a scan that
  // would have failed into one that matches, never the reverse. Do not remove
  // them while "restoring" the pre-extraction behaviour.
  const materialCode = value.slice(0, pipe).trim()
  const lotPart = value.slice(pipe + SEPARATOR.length).trim()
  if (!materialCode) return null
  if (!lotPart.toUpperCase().startsWith(LOT_PREFIX)) return null

  const lotNumber = lotPart.slice(LOT_PREFIX.length).trim()
  if (!lotNumber) return null

  return { materialCode, lotNumber }
}
